/* eslint-disable react/prop-types */
import { FC, useEffect, useRef, useState } from "react";
import {
	ChevronUp,
	ChevronDown,
	Trash2,
	Plus,
	Type,
	ImagePlus,
	Video,
	Upload,
	GripVertical
} from "lucide-react";
import RichTextEditor from "../RichTextEditor/RichTextEditor";

export interface ContentBlock {
	id: string;
	type: "text" | "image" | "video";
	content: string;
	file?: File | null;
	previewUrl?: string | null;
}

interface Props {
	blocks: ContentBlock[];
	onChange: (blocks: ContentBlock[]) => void;
}

const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png"];
const ALLOWED_VIDEO_TYPES = ["video/mp4", "video/webm"];
const MAX_IMAGE_SIZE = 10_485_760; // 10 MB
const MAX_VIDEO_SIZE = 209_715_200; // 200 MB

let blockCounter = 0;
const generateId = () => `block-${++blockCounter}-${Date.now()}`;

const ImageBlock: FC<{
	block: ContentBlock;
	onUpdate: (block: ContentBlock) => void;
}> = ({ block, onUpdate }) => {
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [isDragging, setIsDragging] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const displayUrl = block.previewUrl || block.content;

	useEffect(() => {
		const url = block.previewUrl;
		if (url) {
			return () => URL.revokeObjectURL(url);
		}
	}, [block.previewUrl]);

	const handleFile = (file: File) => {
		if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
			setError("Only JPG and PNG files are allowed.");
			return;
		}
		if (file.size > MAX_IMAGE_SIZE) {
			setError("Image must be under 10 MB.");
			return;
		}
		setError(null);
		const url = URL.createObjectURL(file);
		onUpdate({ ...block, file, previewUrl: url, content: file.name });
	};

	return (
		<div>
			<input
				ref={fileInputRef}
				type="file"
				accept="image/jpeg,image/jpg,image/png"
				className="hidden"
				onChange={(e) => {
					const file = e.target.files?.[0];
					if (file) handleFile(file);
				}}
			/>
			{displayUrl ? (
				<>
					<div className="mb-3 rounded-lg overflow-hidden border border-border bg-subtle max-h-[400px]">
						<img
							src={displayUrl}
							alt="Content image"
							className="object-contain w-full h-auto max-h-[400px]"
						/>
					</div>
					<div className="flex items-center gap-2">
						<button
							type="button"
							className="inline-flex items-center justify-center px-5 h-9 rounded-lg border border-foreground bg-white text-foreground text-sm font-medium cursor-pointer hover:bg-foreground hover:text-white transition-colors"
							onClick={() => fileInputRef.current?.click()}
						>
							Change image
						</button>
						<button
							type="button"
							className="inline-flex items-center justify-center px-5 h-9 rounded-lg border border-red-300 bg-white text-red-600 text-sm font-medium cursor-pointer hover:bg-red-600 hover:text-white hover:border-red-600 transition-colors"
							onClick={() =>
								onUpdate({
									...block,
									file: null,
									previewUrl: null,
									content: ""
								})
							}
						>
							Remove
						</button>
					</div>
				</>
			) : (
				<div
					role="button"
					tabIndex={0}
					onClick={() => fileInputRef.current?.click()}
					onKeyDown={(e) => {
						if (e.key === "Enter" || e.key === " ") {
							e.preventDefault();
							fileInputRef.current?.click();
						}
					}}
					onDragOver={(e) => {
						e.preventDefault();
						setIsDragging(true);
					}}
					onDragLeave={(e) => {
						e.preventDefault();
						setIsDragging(false);
					}}
					onDrop={(e) => {
						e.preventDefault();
						setIsDragging(false);
						const file = e.dataTransfer.files[0];
						if (file) handleFile(file);
					}}
					className={[
						"flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed px-6 py-8 text-center transition-colors cursor-pointer",
						isDragging
							? "border-primary bg-primary/10"
							: "border-border bg-white hover:border-primary hover:bg-primary/5"
					].join(" ")}
				>
					<Upload className="h-5 w-5 text-muted-foreground" />
					<div>
						<p className="text-sm font-medium text-foreground">
							Drag image here or click to browse
						</p>
						<p className="text-xs text-muted-foreground mt-1">
							JPG or PNG, under 10 MB
						</p>
					</div>
				</div>
			)}
			{error && <p className="text-sm text-red-500 mt-2">{error}</p>}
		</div>
	);
};

const VideoBlock: FC<{
	block: ContentBlock;
	onUpdate: (block: ContentBlock) => void;
}> = ({ block, onUpdate }) => {
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [isDragging, setIsDragging] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const displayUrl = block.previewUrl || block.content;

	useEffect(() => {
		const url = block.previewUrl;
		if (url) {
			return () => URL.revokeObjectURL(url);
		}
	}, [block.previewUrl]);

	const handleFile = (file: File) => {
		if (!ALLOWED_VIDEO_TYPES.includes(file.type)) {
			setError("Only MP4 and WebM files are allowed.");
			return;
		}
		if (file.size > MAX_VIDEO_SIZE) {
			setError("Video must be under 200 MB.");
			return;
		}
		setError(null);
		const url = URL.createObjectURL(file);
		onUpdate({ ...block, file, previewUrl: url, content: file.name });
	};

	return (
		<div>
			<input
				ref={fileInputRef}
				type="file"
				accept="video/mp4,video/webm"
				className="hidden"
				onChange={(e) => {
					const file = e.target.files?.[0];
					if (file) handleFile(file);
				}}
			/>
			{displayUrl ? (
				<>
					<div className="mb-3 rounded-lg overflow-hidden border border-border bg-subtle">
						<video controls src={displayUrl} className="w-full max-h-[400px]" />{" "}
						{/* eslint-disable-line */}
					</div>
					<div className="flex items-center gap-2">
						<button
							type="button"
							className="inline-flex items-center justify-center px-5 h-9 rounded-lg border border-foreground bg-white text-foreground text-sm font-medium cursor-pointer hover:bg-foreground hover:text-white transition-colors"
							onClick={() => fileInputRef.current?.click()}
						>
							Change video
						</button>
						<button
							type="button"
							className="inline-flex items-center justify-center px-5 h-9 rounded-lg border border-red-300 bg-white text-red-600 text-sm font-medium cursor-pointer hover:bg-red-600 hover:text-white hover:border-red-600 transition-colors"
							onClick={() =>
								onUpdate({
									...block,
									file: null,
									previewUrl: null,
									content: ""
								})
							}
						>
							Remove
						</button>
					</div>
				</>
			) : (
				<div
					role="button"
					tabIndex={0}
					onClick={() => fileInputRef.current?.click()}
					onKeyDown={(e) => {
						if (e.key === "Enter" || e.key === " ") {
							e.preventDefault();
							fileInputRef.current?.click();
						}
					}}
					onDragOver={(e) => {
						e.preventDefault();
						setIsDragging(true);
					}}
					onDragLeave={(e) => {
						e.preventDefault();
						setIsDragging(false);
					}}
					onDrop={(e) => {
						e.preventDefault();
						setIsDragging(false);
						const file = e.dataTransfer.files[0];
						if (file) handleFile(file);
					}}
					className={[
						"flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed px-6 py-8 text-center transition-colors cursor-pointer",
						isDragging
							? "border-primary bg-primary/10"
							: "border-border bg-white hover:border-primary hover:bg-primary/5"
					].join(" ")}
				>
					<Video className="h-5 w-5 text-muted-foreground" />
					<div>
						<p className="text-sm font-medium text-foreground">
							Drag video here or click to browse
						</p>
						<p className="text-xs text-muted-foreground mt-1">
							MP4 or WebM, under 200 MB
						</p>
					</div>
				</div>
			)}
			{error && <p className="text-sm text-red-500 mt-2">{error}</p>}
		</div>
	);
};

const blockTypeLabels: Record<ContentBlock["type"], string> = {
	text: "Text",
	image: "Image",
	video: "Video"
};

const blockTypeIcons: Record<
	ContentBlock["type"],
	FC<{ className?: string }>
> = {
	text: Type,
	image: ImagePlus,
	video: Video
};

const ContentBlocksEditor: FC<Props> = ({ blocks, onChange }) => {
	const [showAddMenu, setShowAddMenu] = useState(false);

	const updateBlock = (index: number, updated: ContentBlock) => {
		const next = [...blocks];
		next[index] = updated;
		onChange(next);
	};

	const removeBlock = (index: number) => {
		onChange(blocks.filter((_, i) => i !== index));
	};

	const moveBlock = (index: number, direction: "up" | "down") => {
		const target = direction === "up" ? index - 1 : index + 1;
		if (target < 0 || target >= blocks.length) return;
		const next = [...blocks];
		[next[index], next[target]] = [next[target], next[index]];
		onChange(next);
	};

	const addBlock = (type: ContentBlock["type"]) => {
		const newBlock: ContentBlock = {
			id: generateId(),
			type,
			content: "",
			file: null,
			previewUrl: null
		};
		onChange([...blocks, newBlock]);
		setShowAddMenu(false);
	};

	return (
		<div className="flex flex-col gap-3">
			{blocks.map((block, index) => {
				const Icon = blockTypeIcons[block.type];
				return (
					<div
						key={block.id}
						className="rounded-lg border border-border bg-white"
					>
						{/* Block header */}
						<div className="flex items-center gap-2 px-3 py-2 border-b border-border bg-subtle rounded-t-lg">
							<GripVertical className="h-4 w-4 text-zinc-300" />
							<Icon className="h-4 w-4 text-muted-foreground" />
							<span className="text-sm font-medium text-muted-foreground">
								{blockTypeLabels[block.type]}
							</span>

							<div className="ml-auto flex items-center gap-1">
								<button
									type="button"
									onClick={() => moveBlock(index, "up")}
									disabled={index === 0}
									className="p-1 rounded hover:bg-muted disabled:opacity-30 disabled:pointer-events-none transition-colors"
									title="Move up"
								>
									<ChevronUp className="h-4 w-4 text-muted-foreground" />
								</button>
								<button
									type="button"
									onClick={() => moveBlock(index, "down")}
									disabled={index === blocks.length - 1}
									className="p-1 rounded hover:bg-muted disabled:opacity-30 disabled:pointer-events-none transition-colors"
									title="Move down"
								>
									<ChevronDown className="h-4 w-4 text-muted-foreground" />
								</button>
								<button
									type="button"
									onClick={() => removeBlock(index)}
									className="p-1 rounded hover:bg-muted transition-colors"
									title="Remove block"
								>
									<Trash2 className="h-4 w-4 text-red-500" />
								</button>
							</div>
						</div>

						{/* Block content */}
						<div className="p-3">
							{block.type === "text" && (
								<RichTextEditor
									content={block.content}
									onChange={(html) =>
										updateBlock(index, {
											...block,
											content: html
										})
									}
									placeholder="Write content..."
								/>
							)}
							{block.type === "image" && (
								<ImageBlock
									block={block}
									onUpdate={(updated) => updateBlock(index, updated)}
								/>
							)}
							{block.type === "video" && (
								<VideoBlock
									block={block}
									onUpdate={(updated) => updateBlock(index, updated)}
								/>
							)}
						</div>
					</div>
				);
			})}

			{/* Add block button */}
			<div className="relative">
				<button
					type="button"
					onClick={() => setShowAddMenu(!showAddMenu)}
					className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-dashed border-border bg-white text-sm font-medium text-muted-foreground hover:border-primary hover:text-primary transition-colors"
				>
					<Plus className="h-4 w-4" />
					Add content block
				</button>

				{showAddMenu && (
					<div className="absolute top-full left-0 mt-1 z-10 bg-white border border-border rounded-lg shadow-lg py-1 w-48">
						<button
							type="button"
							onClick={() => addBlock("text")}
							className="flex items-center gap-2.5 w-full px-4 py-2 text-sm hover:bg-muted text-left"
						>
							<Type className="h-4 w-4 text-muted-foreground" />
							<span className="text-foreground">Text</span>
						</button>
						<button
							type="button"
							onClick={() => addBlock("image")}
							className="flex items-center gap-2.5 w-full px-4 py-2 text-sm hover:bg-muted text-left"
						>
							<ImagePlus className="h-4 w-4 text-muted-foreground" />
							<span className="text-foreground">Image</span>
						</button>
						<button
							type="button"
							onClick={() => addBlock("video")}
							className="flex items-center gap-2.5 w-full px-4 py-2 text-sm hover:bg-muted text-left"
						>
							<Video className="h-4 w-4 text-muted-foreground" />
							<span className="text-foreground">Video</span>
						</button>
					</div>
				)}
			</div>
		</div>
	);
};

export default ContentBlocksEditor;
